import type { ReactNode } from "react";
import { useMemo, useState } from "react";

import isEqual from "lodash/isEqual";

import { DesktopComputer, MobileDevice } from "@similarweb/icons";
import type {
    MonitorAndProtectModelSharedRequestDeviceType,
    MonitorAndProtectModelTrademarksAdsViolationType,
} from "@similarweb/pro-api-client";
import { filterFactory } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Flex } from "@similarweb/ui-components/foundations";

import CountryService from "app/services/CountryService";
import { getFaviconUrl } from "app/services/sitesService/sitesUtils";
import { useTranslation } from "pro-features/components/WithTranslation";

import { mnpTrademarkNavigation } from "../../../app/navigation";
import { useMonitorAndProtectCampaigns } from "../../../services/api/campaign";
import { DomainsIncludeExcludeRenderer } from "../../../shared/components/filter-factory-items/domains-include-exclude/include-exclude-renderer";
import {
    createIncludeExcludeLabel,
    createMultiSelectLabel,
    createSelectLabel,
} from "../../../shared/filters/label-helpers";
import { useMonitorAndProtectTrademarkDataAndParams } from "../../../shared/hooks/trademark-params";
import { Favicon } from "../../../shared/ui/layout/favicon";

import type { ITrademarkFiltersState } from "./trademark-filters.types";

const Filters = filterFactory.createTypedFilters<Partial<ITrademarkFiltersState>>();

const sortBySelectedFirst = <T extends { id: string; label: string }>(options: T[], selectedIds: Set<string>): T[] =>
    [...options].sort((a, b) => {
        const aSelected = selectedIds.has(a.id);
        const bSelected = selectedIds.has(b.id);
        if (aSelected !== bSelected) {
            return aSelected ? -1 : 1;
        }
        return a.label.localeCompare(b.label);
    });
interface IDeviceSelectOption {
    id: MonitorAndProtectModelSharedRequestDeviceType;
    value: MonitorAndProtectModelSharedRequestDeviceType;
    label: string;
    icon?: ReactNode;
    [key: string]: unknown;
}

interface IViolationSelectOption {
    id: MonitorAndProtectModelTrademarksAdsViolationType;
    value: MonitorAndProtectModelTrademarksAdsViolationType;
    label: string;
    icon?: ReactNode;
    [key: string]: unknown;
}

export const TrademarkFilters = () => {
    const translate = useTranslation();
    const trademarksParams = useMonitorAndProtectTrademarkDataAndParams();
    const [state, setState] = useState<Partial<ITrademarkFiltersState>>(trademarksParams.dataFilters);
    const { data: campaigns = [] } = useMonitorAndProtectCampaigns({ includeDemoCampaigns: false });

    const deviceTypes: Array<IDeviceSelectOption> = useMemo(
        () => [
            {
                id: "MobileWeb",
                value: "MobileWeb",
                label: translate("monitor_and_protect.features.trademark_dashboard.filters.device_type_mobile"),
                icon: (
                    <MobileDevice
                        size="sm"
                        color={colorsPalettes.carbon["200"]}
                    />
                ),
            },
            {
                id: "Desktop",
                value: "Desktop",
                label: translate("monitor_and_protect.features.trademark_dashboard.filters.device_type_desktop"),
                icon: (
                    <DesktopComputer
                        size="sm"
                        color={colorsPalettes.carbon["200"]}
                    />
                ),
            },
        ],
        [translate],
    );

    const violationTypes: Array<IViolationSelectOption> = useMemo(
        () => [
            {
                id: "AdCopy",
                value: "AdCopy",
                label: translate("monitor_and_protect.features.trademark_dashboard.filters.violation_type_ad_copy"),
            },
            {
                id: "Keyword",
                value: "Keyword",
                label: translate("monitor_and_protect.features.trademark_dashboard.filters.violation_type_keyword"),
            },
        ],
        [translate],
    );

    const handleFilterStateChange = (newState: Partial<ITrademarkFiltersState>) => {
        setState(newState);

        if (isEqual(state, newState)) {
            return;
        }

        mnpTrademarkNavigation.updateQueryParams(
            {
                dataFilters: newState,
            },
            { reload: true },
        );
    };

    const locations = useMemo(() => {
        const allLocations = [...new Set(campaigns?.flatMap((e) => e.locations).map((e) => e.countryCode) ?? [])];
        const parsedLocations = allLocations.map((e) => CountryService.getCountryById(e.toString()));
        return parsedLocations;
    }, [campaigns]);

    const campaignOptions = useMemo(() => {
        const options = campaigns.map((e) => ({
            id: e.id,
            label: e.name,
            value: e.id,
            icon: (
                <Favicon
                    iconUrl={getFaviconUrl(e.domain)}
                    alt={e.name}
                />
            ),
        }));
        
        return sortBySelectedFirst(options, new Set(state.campaignsIds ?? []));
    }, [campaigns, state.campaignsIds]);

    const locationOptions = useMemo(() => {
        const options = locations.map((e) => ({
            id: e.id.toString(),
            label: e.text,
            value: e.text,
            location: e,
        }));

        return sortBySelectedFirst(options, new Set(state.locations ?? []));
    }, [locations, state.locations]);

    return (
        <Filters.Provider
            state={state}
            onChange={handleFilterStateChange}
        >
            <Flex gap="12px">
                <Filters.MultiSelect
                    name="campaignsIds"
                    label={createMultiSelectLabel(
                        translate("monitor_and_protect.features.trademark_dashboard.filters.campaigns"),
                        (id) => campaigns.find((c) => c.id === id),
                        (c) => c.name,
                    )}
                    withSearch
                    options={campaignOptions}
                />
                <Filters.MultiSelect
                    name="locations"
                    label={createMultiSelectLabel(
                        translate("monitor_and_protect.features.trademark_dashboard.filters.locations"),
                        (id) => locations.find((l) => l.id.toString() === id),
                        (location) => location.text,
                    )}
                    withSearch
                    options={locationOptions}
                />
                <Filters.Select
                    name="deviceType"
                    label={createSelectLabel(
                        translate("monitor_and_protect.features.trademark_dashboard.filters.device_type"),
                        (id) => deviceTypes.find((d) => d.value === id),
                        (device) => device.label,
                    )}
                    withSearch={false}
                    width={250}
                    options={deviceTypes}
                />
                <Filters.Custom
                    name="domains"
                    actionable
                    commitMode="close"
                    label={createIncludeExcludeLabel({
                        defaultLabel: translate("monitor_and_protect.features.trademark_dashboard.filters.domains"),
                        includedLabel: translate(
                            "monitor_and_protect.features.trademark_dashboard.filters.domains_included",
                        ),
                        excludedLabel: translate(
                            "monitor_and_protect.features.trademark_dashboard.filters.domains_excluded",
                        ),
                    })}
                >
                    {(api) => (
                        <DomainsIncludeExcludeRenderer
                            state={api.tempValue}
                            onChange={api.onTempChange}
                            texts={{
                                includeTitle: translate(
                                    "monitor_and_protect.features.trademark_dashboard.filters.include_advertisers_title",
                                ),
                                excludeTitle: translate(
                                    "monitor_and_protect.features.trademark_dashboard.filters.exclude_advertisers_title",
                                ),
                            }}
                        />
                    )}
                </Filters.Custom>
                <Filters.Select
                    name="violationType"
                    label={createSelectLabel(
                        translate("monitor_and_protect.features.trademark_dashboard.filters.violation_type"),
                        (id) => violationTypes.find((v) => v.value === id),
                        (violation) => violation.label,
                    )}
                    width={250}
                    withSearch={false}
                    options={violationTypes}
                />
                <Filters.ClearAll />
            </Flex>
        </Filters.Provider>
    );
};
